import { isFinanceProject, isGlobalProject } from 'config';
import { subYears } from 'date-fns';
import { applySchema } from 'helpers';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  object,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import {
  BaseDebtsFormType,
  getBaseDebtsSchema,
} from 'modules/client/common/forms/baseDebtsForm';
import {
  ReasonChangeFormType,
  reasonChangeSchema,
} from 'modules/client/common/forms/reasonChangeForm';
import { residentialAddressWithFilesSchema } from 'modules/client/common/forms/residentialAddressForm';

import { ActivityFormType, activitySchema } from './activityForm';
import {
  AdditionalDocumentsFormType,
  getAdditionalDocumentsSchema,
} from './additionalDocumentsForm';
import { BankAccountsFormType, bankAccountsSchema } from './bankAccountsForm';
import {
  BasisForRepresentationFormType,
  getBasisForRepresentationSchema,
} from './basisForRepresentationForm';
import { BiographyFormType, biographySchema } from './biographyForm';
import { CitizenshipFormType, citizenshipsSchema } from './citizenshipForm';
import { ContactsFormType, getContactsSchema } from './contactsForm';
import {
  IdentityDocumentFormType,
  identityDocumentSchema,
} from './identityDocumentForm';
import {
  PepConnectionFormType,
  getPepConnectionSchema,
} from './pepConnectionForm';
import {
  PersonalDataFormType,
  getPersonalDataSchema,
} from './personalDataForm';
import { PlaceOfBirthFormType, placeOfBirthSchema } from './placeOfBirthForm';
import {
  RegistrationAddressFormType,
  registrationAddressSchema,
} from './registrationAddressForm';
import { ResidentialAddressFormType } from './residentialAddressForm';
import {
  TaxResidencyFormType,
  getTaxResidencesSchema,
} from './taxResidencyForm';

export interface RepresentativeFormType
  extends ActivityFormType,
    ContactsFormType,
    PersonalDataFormType,
    CitizenshipFormType,
    TaxResidencyFormType,
    PlaceOfBirthFormType,
    RegistrationAddressFormType,
    ResidentialAddressFormType,
    BiographyFormType,
    BankAccountsFormType,
    PepConnectionFormType,
    BaseDebtsFormType,
    BasisForRepresentationFormType,
    IdentityDocumentFormType,
    AdditionalDocumentsFormType,
    ReasonChangeFormType {}

export const dateOfBirthMaxDate = subYears(new Date(), 18);
export const dateOfBirthMinDate = subYears(new Date(), 100);

export const getSchema = (t: TFunc): ObjectSchema<RepresentativeFormType> => {
  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();
  return object()
    .concat(getContactsSchema(t))
    .concat(getPersonalDataSchema(t, { enabledGender: isGlobal }))
    .concat(citizenshipsSchema)
    .concat(getTaxResidencesSchema(isGlobal))
    .concat(applySchema(isFinance, registrationAddressSchema))
    .concat(residentialAddressWithFilesSchema)
    .concat(placeOfBirthSchema)
    .concat(applySchema(isFinance, biographySchema))
    .concat(applySchema(isFinance, bankAccountsSchema))
    .concat(activitySchema)
    .concat(identityDocumentSchema)
    .concat(applySchema(isFinance, getPepConnectionSchema(t)))
    .concat(getBasisForRepresentationSchema(t))
    .concat(reasonChangeSchema)
    .concat(getAdditionalDocumentsSchema())
    .concat(applySchema(isFinance, getBaseDebtsSchema()));
};

export const { Field, Form, useFormContext, Submit } =
  TypedForm<RepresentativeFormType>();

export type FormErrors = FormErrorsBase<RepresentativeFormType>;
